import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import {
	useActivePhoto,
	useSetActivePhoto,
} from '@context/ActivePhotoContext'
import Preview from '@components/display/photos/PreviewDynamic'
import Controls from '@components/display/photos/LightboxControlsDynamic'
import ImageWithFallback from '@components/image/WithFallback'
import Inscription from '../offer/Inscription'
import Video from '@components/display/video/Video'
import { ThumbsScrollController } from './ThumbsScrollController'
import cloudflarePhoto from '@utils/links/cloudflarePhoto'
import makeYoutubeThumbnailResolver from '@utils/video/makeYoutubeThumbnailResolver'
import useMediaQueryBreakpoint from '@hooks/useMediaQueryBreakpoint'
import resolveCloudflareSrcSet from '@components/image/functions/resolveCloudflareSrcSet'
import mix from '@utils/styles/mix'

import stylesCf from '@components/image/OfferImage.module.css'
import styles from './MainPhoto.module.css'

const VID_PREFIX = 'vid:'
const MAIN_PHOTO_VIEW_SRC_SET = {
	'1x': 'x1view',
	'2x': 'x2view',
}

function Carousel({ alt, fallback, offer, scrollToChild }) {
	const { tracking } = offer || {}
	const { cloudflare } = tracking || {}
	const { mainPhoto, photos: cfPhotos } = cloudflare || {}

	const photos = useMemo(
		() => (cfPhotos?.length ? cfPhotos : [mainPhoto]),
		[mainPhoto, cfPhotos],
	)

	const [showFullScreen, setShowFullScreen] = useState(false)

	const mobile = useMediaQueryBreakpoint(800)
	const photoRef = useRef()
	const photosRef = useRef([])
	const thumbsRef = useRef()
	const thumbRefs = useRef([])

	const video = offer?.video_link ? VID_PREFIX + offer.video_link : ''
	const videoThumbnail =
		video && makeYoutubeThumbnailResolver(video)()

	const [photoOrders, photoLinksOrIds] = useMemo(() => {
		const orders = Array.from(
			{ length: photos?.length || 0 },
			(_v, k) => k,
		)
		return [orders, video ? [video].concat(photos) : photos]
	}, [photos, video])

	const currentActivePhoto = useActivePhoto()
	const setActivePhoto = useSetActivePhoto()

	const scrollWrappersToDesiredPhoto = useCallback(
		(id) => {
			scrollToChild(photoRef.current, photosRef.current[id])
			scrollToChild(thumbsRef.current, thumbRefs.current[id])
		},
		[photoRef, scrollToChild, thumbsRef],
	)

	const togglePhoto = useCallback(
		(id) => () => {
			const max = photoLinksOrIds.length - 1
			if (id < 0) setActivePhoto(max)
			if (id > max) setActivePhoto(0)
			else setActivePhoto(id)
		},
		[photoLinksOrIds, setActivePhoto],
	)

	const showFullScreenPreview = useCallback(() => {
		setShowFullScreen((bool) => !bool)
	}, [])

	const toggleLightbox = useMemo(
		() =>
			photoLinksOrIds.length ? showFullScreenPreview : undefined,
		[photoLinksOrIds, showFullScreenPreview],
	)

	const controlMin = 0
	const controlMax =
		(photoOrders?.length ? photoOrders.length - 1 : controlMin) +
		(video ? 1 : 0)

	const controls = <Controls max={controlMax} min={controlMin} />

	const previewId = photoLinksOrIds[currentActivePhoto] || ''

	useEffect(() => {
		scrollWrappersToDesiredPhoto(currentActivePhoto)
	}, [currentActivePhoto, scrollWrappersToDesiredPhoto])

	return (
		<>
			{showFullScreen && (
				<Preview
					className={stylesCf['cf-image']}
					controls={controls}
					count={photoLinksOrIds.length}
					lp={currentActivePhoto + 1}
					photo={
						previewId?.startsWith(VID_PREFIX)
							? videoThumbnail
							: cloudflarePhoto(previewId, 'x2view')
					}
					toggle={toggleLightbox}
					useNextImage={false}
				/>
			)}

			<div className={styles.carousel}>
				<div className={styles.container} ref={photoRef}>
					{photoLinksOrIds.map((linkOrId, id) => {
						const isVideo = linkOrId.startsWith(VID_PREFIX)
						const firstImageIdx = video ? 1 : 0
						const isFirstImage = id === firstImageIdx
						const inscription =
							isFirstImage && offer?.data?.inscription
						const reserved = isFirstImage && offer?.tracking?.reserved

						return (
							<div
								className={mix([
									styles.slide,
									isVideo && styles.video,
								])}
								key={linkOrId}
								ref={(el) => (photosRef.current[id] = el)}
							>
								{isVideo ? (
									<Video offer={offer} />
								) : (
									<>
										{(inscription || reserved) && (
											<Inscription
												reserved={reserved}
												text={inscription}
												big
											/>
										)}
										<ImageWithFallback
											alt={alt || ''}
											className={stylesCf['cf-image-contain']}
											draggable={false}
											fallback={fallback}
											onClick={toggleLightbox}
											priority={isFirstImage}
											src={cloudflarePhoto(linkOrId, 'x1view')}
											srcSet={resolveCloudflareSrcSet(
												MAIN_PHOTO_VIEW_SRC_SET,
												linkOrId,
											)}
											useNextImage={false}
										/>
									</>
								)}
							</div>
						)
					})}
				</div>

				{controls}
			</div>

			{!mobile && photoLinksOrIds.length > 1 && (
				<ThumbsScrollController forwardRef={thumbsRef}>
					<div className={styles.thumbnails} ref={thumbsRef}>
						{photoLinksOrIds.map((linkOrId, id) => {
							const isVideo = linkOrId.startsWith(VID_PREFIX)
							const src = isVideo
								? makeYoutubeThumbnailResolver(
										linkOrId.slice(VID_PREFIX.length),
								  )()
								: `${cloudflarePhoto(linkOrId, 'thumb')}?ffoxfix=1`

							return (
								<span
									key={linkOrId}
									onClick={togglePhoto(id)}
									ref={(el) => (thumbRefs.current[id] = el)}
									style={{ paddinTop: 0 }}
								>
									<ImageWithFallback
										alt={alt || ''}
										className={stylesCf['cf-image']}
										draggable={false}
										fallback={fallback}
										spinner={false}
										src={src}
										useNextImage={false}
									/>
								</span>
							)
						})}
					</div>
				</ThumbsScrollController>
			)}
		</>
	)
}

export default Carousel